import { Switch } from "antd";
import { ColumnProps } from "antd/lib/table";
import { EMAIL_REMINDER_CONFIG_ROUTE } from "config/route-consts";
import { renderMasterIndex } from "helpers/table";
import { EmailRemindConfig } from "models/EmailRemindConfig";
import { EmailRemindConfigCalendar } from "models/EmailRemindConfig/EmailRemindConfigCalendar";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { LayoutCell, LayoutHeader, OneLineText } from "react3l-ui-library";
import { emailRemindConfigRepository } from "repositories/email-remind-config-repository";
import { detailService } from "services/page-services/detail-service";
import { fieldService } from "services/page-services/field-service";
import { filterService } from "services/page-services/filter-service";
import { listService } from "services/page-services/list-service";
import nameof from "ts-nameof.macro";

function useEmailRemindConfigPreviewHook() {
  const [translate] = useTranslation();

  const { model, dispatch } =
    detailService.useModel<EmailRemindConfig>(EmailRemindConfig);

  const { isDetail } = detailService.useGetIsDetail<EmailRemindConfig>(
    emailRemindConfigRepository.get,
    dispatch
  );

  const loadingPreview = isDetail && !model?.id;

  const { handleChangeAllField } = fieldService.useField(model, dispatch);

  const { modelFilter: emailReminderConfigFilter } =
    filterService.useModelFilter(EmailRemindConfigCalendar);

  const { list: listEmailReminderSchedules } = listService.useLocalList(
    model?.emailReminderSchedules,
    emailReminderConfigFilter
  );
  const { handleGoMaster } = detailService.useActionsDetail<EmailRemindConfig>(
    model,
    emailRemindConfigRepository.save,
    handleChangeAllField,
    EMAIL_REMINDER_CONFIG_ROUTE
  );

  const columns: ColumnProps<EmailRemindConfig>[] = useMemo(() => {
    return [
      {
        title: (
          <div className="text-center gradient-text">
            {translate("general.columns.orderNumber")}
          </div>
        ),
        key: "index",
        width: 80,
        align: "center",
        fixed: "left",
        render: renderMasterIndex<EmailRemindConfig>(),
      },
      {
        title: (
          <LayoutHeader
            orderType="left"
            title={translate("emailRemindConfig.reminderTable.timeAgo")}
          />
        ),
        key: "beforeValue",
        dataIndex: "beforeValue",
        render(...params: [number, EmailRemindConfigCalendar, number]) {
          return (
            <LayoutCell orderType="left" tableSize="lg">
              <OneLineText value={params[0].toString()} />
            </LayoutCell>
          );
        },
      },
      {
        title: (
          <LayoutHeader
            orderType="left"
            title={translate("emailRemindConfig.reminderTable.timeUnit")}
          />
        ),
        key: "timeUnit",
        dataIndex: "timeUnit",
        render(...params: [string, EmailRemindConfigCalendar, number]) {
          return (
            <LayoutCell orderType="left" tableSize="lg">
              <OneLineText value={params[0]} />
            </LayoutCell>
          );
        },
      },
      {
        title: (
          <LayoutHeader
            orderType="left"
            title={translate("emailRemindConfig.description")}
          />
        ),
        key: "description",
        dataIndex: "description",
        render(...params: [string, EmailRemindConfigCalendar, number]) {
          return (
            <LayoutCell orderType="left" tableSize="lg">
              <OneLineText value={params[0]} />
            </LayoutCell>
          );
        },
      },
      {
        title: (
          <LayoutHeader
            orderType="left"
            title={translate("emailRemindConfig.status")}
          />
        ),
        width: 120,
        key: nameof("order"),
        dataIndex: nameof("order"),
        align: "center",
        render(...params: [number, EmailRemindConfigCalendar, number]) {
          return (
            <LayoutCell orderType="center" tableSize="lg">
              <Switch
                checked={params[1].statusId === 1 ? true : false}
                disabled
              />
            </LayoutCell>
          );
        },
      },
    ];
  }, [translate]);

  return {
    translate,
    handleGoMaster,
    model,
    listEmailReminderSchedules,
    columns,
    loadingPreview,
  };
}
export default useEmailRemindConfigPreviewHook;
